// This component should support image size styles so possibly some of the styles will not be used.

import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { findFirstNonEmptyString } from "scmp-sdk";

import type { ResponsiveVariants } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import type {
  Props as ArticleImageProps,
  CarouselSlide,
} from "scmp-app/components/article/article-image";
import { ArticleImage } from "scmp-app/components/article/article-image";
import type { Props as BaseImageProps } from "scmp-app/components/common/base-image";
import { useResponsiveImage } from "scmp-app/components/common/base-image/hooks";
import { useImageUtilities } from "scmp-app/lib/hooks";
import type { articleLeadingImagesContent$key } from "scmp-app/queries/__generated__/articleLeadingImagesContent.graphql";

import { ArticleLeadingImageContainer } from "./styles";

const variantSpecsForImage = {
  size660w: { aspectRatio: "auto", width: 1320 },
  size768x768: { aspectRatio: "1/1", width: 768 },
  size1020x680: { aspectRatio: "3/2", width: 1020 },
  size1200x800: { aspectRatio: "3/2", width: 1200 },
  size1280x720: { aspectRatio: "16/9", width: 1280 },
  size1676x943: { aspectRatio: "16/9", width: 1676 },
  size1320w: { aspectRatio: "auto", width: 1320 },
  sizeLandscape250x99: { aspectRatio: "250/99", width: 1024 },
};

export type Props = Pick<
  ArticleImageProps,
  "responsiveDescriptionVariants" | "responsiveImageVariants" | "skipGallery"
> & {
  className?: string;
  imageProps: Omit<BaseImageProps, "$aspectRatio">;
  reference: articleLeadingImagesContent$key;
  resizeImageWidth?: number;
  responsiveVariants: ResponsiveVariants<
    | "size660w"
    | "size768x768"
    | "size1020x680"
    | "size1200x800"
    | "size1280x720"
    | "size1676x943"
    | "size1320w"
    | "sizeLandscape250x99"
  >;
  showTitle?: boolean;
  toggleTitle?: boolean;
  withCarousel?: { imageType: string };
};

export const ArticleLeadingImages: FunctionComponent<Props> = ({
  className,
  imageProps,
  reference,
  resizeImageWidth,
  responsiveVariants,
  showTitle = true,
  toggleTitle = false,
  withCarousel,
  ...articleImageProps
}) => {
  const content = useFragment(
    graphql`
      fragment articleLeadingImagesContent on Content {
        headline
        images {
          title
          type
          url
          isSlideshow
          size660w: style(filter: { style: "660w" }) {
            url
          }
          size1320w: style(filter: { style: "1320w" }) {
            url
          }
          sizeLandscape250x99: style(filter: { style: "landscape_250_99" }) {
            url
          }
          size1280x720: style(filter: { style: "wide_landscape" }) {
            url
          }
          size1676x943: style(filter: { style: "wide_landscape_style" }) {
            url
          }
          size1200x800: style(filter: { style: "1200x800" }) {
            url
          }
          size1020x680: style(filter: { style: "1020x680" }) {
            url
          }
          size768x768: style(filter: { style: "768x768" }) {
            url
          }
        }
        ... on Article {
          leadingSlides {
            title
            type
            url
            size660w: style(filter: { style: "660w" }) {
              url
            }
            size1320w: style(filter: { style: "1320w" }) {
              url
            }
            sizeLandscape250x99: style(filter: { style: "landscape_250_99" }) {
              url
            }
            size1280x720: style(filter: { style: "wide_landscape" }) {
              url
            }
            size1676x943: style(filter: { style: "wide_landscape_style" }) {
              url
            }
            size1200x800: style(filter: { style: "1200x800" }) {
              url
            }
            size1020x680: style(filter: { style: "1020x680" }) {
              url
            }
            size768x768: style(filter: { style: "768x768" }) {
              url
            }
          }
        }
      }
    `,
    reference,
  );
  const { getResizedImageUrl } = useImageUtilities();

  const mainImage = content.images?.find(image => image?.isSlideshow) ?? first(content.images);

  const carouselSlides = useMemo<CarouselSlide[]>(() => {
    if (!withCarousel) return [];

    const mapVariantForImage = (
      image: Record<string, unknown> & { url: string },
      viewportSize: "desktopUp" | "mobileUp" | "tabletUp",
    ) => {
      const variantKey = responsiveVariants[viewportSize];
      const spec = variantSpecsForImage[variantKey];
      const sizeUrl = (image as Record<string, undefined | { url?: null | string }>)?.[variantKey]
        ?.url;
      return {
        aspectRatio: spec.aspectRatio,
        url: sizeUrl ?? image.url ?? "",
        width: spec.width,
      };
    };

    const firstImage =
      content.images?.find(image => image?.type === withCarousel.imageType) ??
      first(content.images);
    const leadingSlides = content.leadingSlides ?? [];
    const allImages = [firstImage, ...leadingSlides].filter(notEmpty);
    if (allImages.length <= 1) return [];
    return allImages.map(image => ({
      responsiveVariant: {
        desktopUp: mapVariantForImage(image, "desktopUp"),
        mobileUp: mapVariantForImage(image, "mobileUp"),
        tabletUp: mapVariantForImage(image, "tabletUp"),
      },
      title: image.title,
      url: image.url,
    }));
  }, [withCarousel, content, responsiveVariants]);

  const variantSpecs = {
    size660w: { aspectRatio: "auto", url: mainImage?.size660w?.url, width: 1320 },
    size768x768: { aspectRatio: "1/1", url: mainImage?.size768x768?.url, width: 768 },
    size1020x680: { aspectRatio: "3/2", url: mainImage?.size1020x680?.url, width: 1020 },
    size1200x800: { aspectRatio: "3/2", url: mainImage?.size1200x800?.url, width: 1200 },
    size1280x720: { aspectRatio: "16/9", url: mainImage?.size1280x720?.url, width: 1280 },
    size1676x943: { aspectRatio: "16/9", url: mainImage?.size1676x943?.url, width: 1676 },
    size1320w: { aspectRatio: "auto", url: mainImage?.size1320w?.url, width: 1320 },
    sizeLandscape250x99: {
      aspectRatio: "250/99",
      url: mainImage?.sizeLandscape250x99?.url,
      width: 1024,
    },
  };
  const mapVariant = (viewportSize: "desktopUp" | "mobileUp" | "tabletUp") => ({
    aspectRatio: variantSpecs[responsiveVariants[viewportSize]].aspectRatio,
    url: variantSpecs[responsiveVariants[viewportSize]].url ?? "",
    width: variantSpecs[responsiveVariants[viewportSize]].width,
  });

  const responsiveVariant = {
    desktopUp: mapVariant("desktopUp"),
    mobileUp: mapVariant("mobileUp"),
    tabletUp: mapVariant("tabletUp"),
  };

  const { bindResponsiveAspectRatio } = useResponsiveImage(responsiveVariant);
  if (!mainImage) return null;

  let imageUrl = mainImage[responsiveVariants.mobileUp]?.url ?? mainImage.url;
  imageUrl = resizeImageWidth ? getResizedImageUrl(imageUrl, resizeImageWidth) : imageUrl;

  return (
    <ArticleLeadingImageContainer className={className}>
      <ArticleImage
        carouselSlides={carouselSlides.length > 1 ? carouselSlides : undefined}
        imageProps={{
          alt: findFirstNonEmptyString(mainImage?.title, content.headline),
          ...imageProps,
          $objectFit: "cover",
          responsiveVariant,
          src: imageUrl,
          ...bindResponsiveAspectRatio,
        }}
        skipGallery={true}
        title={showTitle ? (mainImage.title ?? "") : ""}
        toggleTitle={toggleTitle}
        {...articleImageProps}
      />
    </ArticleLeadingImageContainer>
  );
};

ArticleLeadingImages.displayName = "ArticleLeadingImages";
